import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Trash2,
    User,
    FileText,
    DollarSign,
    Package,
    Mail,
    Phone,
    Clock,
    CheckCircle,
    AlertCircle,
    Loader,
    Receipt
} from 'lucide-react';
import { salesAPI } from '../../services/garageApi';

const SaleDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [sale, setSale] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchSale = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await salesAPI.show(id);
            const saleData = response.data;

            if (!saleData) {
                throw new Error('Sale not found');
            }

            setSale(saleData);
        } catch (err) {
            console.error('Error fetching sale:', err);
            setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load sale');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchSale();
    }, [fetchSale]);

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this sale? This will restore inventory.')) {
            return;
        }

        try {
            await salesAPI.delete(id);
            alert('Sale deleted successfully');
            navigate('/sales');
        } catch (err) {
            console.error('Error deleting sale:', err);
            const message = err.response?.data?.error || err.response?.data?.message || 'Failed to delete sale';
            alert('Error: ' + message);
        }
    };

    const getStatusBadge = (status) => {
        const config = {
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
            paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
            partial: { color: 'bg-blue-100 text-blue-800', icon: DollarSign },
            cancelled: { color: 'bg-red-100 text-red-800', icon: AlertCircle },
        };
        return config[status] || { color: 'bg-gray-100 text-gray-800', icon: Clock };
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center">
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Loading sale...
                </div>
            </div>
        );
    }

    if (error || !sale) {
        return (
            <div className="space-y-4">
                <div className="rounded-md bg-red-50 border border-red-200 p-4">
                    <div className="flex items-center space-x-2 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4" />
                        <span>{error || 'Sale not found'}</span>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/sales')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Sales
                </button>
            </div>
        );
    }

    const badge = getStatusBadge(sale.status);
    const BadgeIcon = badge.icon;
    const saleItems = sale.sale_items || sale.saleItems || [];
    const receipts = sale.receipts || [];

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => navigate('/sales')}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Sale #{sale.id}</h1>
                        <p className="text-gray-600">
                            {sale.sale_date ? new Date(sale.sale_date).toLocaleDateString() : ''}
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
                        <BadgeIcon className="h-4 w-4" />
                        <span className="capitalize">{sale.status}</span>
                    </span>

                    {sale.status !== 'paid' && (
                        <div className="flex space-x-2">
                            <button
                                onClick={() => navigate('/receipts/create')}
                                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                            >
                                <DollarSign className="h-4 w-4 mr-1" />
                                Record Payment
                            </button>
                            <button
                                onClick={handleDelete}
                                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                            >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Sale Info */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Customer */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                <User className="h-4 w-4 mr-2" />
                                Customer
                            </h3>
                            {sale.customer ? (
                                <div className="text-sm text-gray-600 space-y-1">
                                    <div className="font-medium text-gray-900">{sale.customer.name}</div>
                                    {sale.customer.email && (
                                        <div className="flex items-center">
                                            <Mail className="h-3 w-3 mr-2" />
                                            {sale.customer.email}
                                        </div>
                                    )}
                                    {sale.customer.phone && (
                                        <div className="flex items-center">
                                            <Phone className="h-3 w-3 mr-2" />
                                            {sale.customer.phone}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">No customer info</p>
                            )}
                        </div>

                        {/* Sale Details */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                <FileText className="h-4 w-4 mr-2" />
                                Sale Details
                            </h3>
                            <div className="text-sm text-gray-600 space-y-2">
                                <div className="flex justify-between">
                                    <span>Sale Date:</span>
                                    <span className="font-medium">
                                        {sale.sale_date ? new Date(sale.sale_date).toLocaleDateString() : '-'}
                                    </span>
                                </div>
                                {sale.invoice_number && (
                                    <div className="flex justify-between">
                                        <span>Invoice:</span>
                                        <button
                                            onClick={() => navigate(`/invoices/${sale.invoice_number}`)}
                                            className="font-medium text-blue-600 hover:underline"
                                        >
                                            {sale.invoice_number}
                                        </button>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span>Total:</span>
                                    <span className="font-bold text-lg text-gray-900">
                                        ${Number(sale.total_amount || 0).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sale Items */}
                <div className="px-6 py-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                        <Package className="h-4 w-4 mr-2" />
                        Items ({saleItems.length})
                    </h3>

                    {saleItems.length > 0 ? (
                        <div className="overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discount</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {saleItems.map((item, index) => (
                                        <tr key={item.id || index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {item.saleable?.name || item.saleable?.battery_name || item.saleable?.size || item.saleable?.service_name || `Item #${index + 1}`}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs capitalize">
                                                    {item.saleable_type || '-'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity || 1}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                ${Number(item.selling_price_at_sale || 0).toFixed(2)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                {Number(item.discount || 0).toFixed(1)}%
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                                ${Number(item.total_price || 0).toFixed(2)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50">
                                    <tr>
                                        <td colSpan={5} className="px-4 py-3 text-sm font-medium text-gray-700 text-right">
                                            Total:
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                            ${Number(sale.total_amount || 0).toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p>No items found</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Receipts / Payments */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-sm font-medium text-gray-900 flex items-center">
                        <Receipt className="h-4 w-4 mr-2" />
                        Payments ({receipts.length})
                    </h3>
                </div>

                {receipts.length > 0 ? (
                    <div className="overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt ID</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {receipts.map((receipt) => (
                                    <tr
                                        key={receipt.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => navigate(`/receipts/${receipt.id}`)}
                                    >
                                        <td className="px-4 py-3 text-sm font-medium text-blue-600">{receipt.id}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {receipt.receipt_date ? new Date(receipt.receipt_date).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600 capitalize">{receipt.payment_method || '-'}</td>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                            ${Number(receipt.amount || 0).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50">
                                <tr>
                                    <td colSpan={3} className="px-4 py-3 text-sm font-medium text-gray-700 text-right">
                                        Total Paid:
                                    </td>
                                    <td className="px-4 py-3 text-sm font-bold text-green-600 text-right">
                                        ${receipts.reduce((sum, r) => sum + Number(r.amount || 0), 0).toFixed(2)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ) : (
                    <div className="px-6 py-8 text-center text-gray-500">
                        <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>No payments recorded yet.</p>
                        {sale.status !== 'paid' && (
                            <button
                                onClick={() => navigate('/receipts/create')}
                                className="mt-3 inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                            >
                                <DollarSign className="h-4 w-4 mr-1" />
                                Record Payment
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SaleDetail;
